import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, type Conversation } from '../api';

const PAGE_SIZE = 20;

export default function Conversations() {
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [page, setPage] = useState(0);
  const [items, setItems] = useState<Conversation[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedQ(q.trim()); setPage(0); }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setLoading(true);
    api
      .listConversations({ limit: PAGE_SIZE, offset: page * PAGE_SIZE, q: debouncedQ || undefined })
      .then((r) => { setItems(r.items); setTotal(r.total); })
      .finally(() => setLoading(false));
  }, [debouncedQ, page]);

  async function del(id: number) {
    if (!confirm('Supprimer cette conversation ?')) return;
    await api.deleteConversation(id);
    // Reload current page; if it becomes empty and we're not on page 0, go back one.
    const r = await api.listConversations({ limit: PAGE_SIZE, offset: page * PAGE_SIZE, q: debouncedQ || undefined });
    if (r.items.length === 0 && page > 0) setPage((p) => p - 1);
    else { setItems(r.items); setTotal(r.total); }
  }

  function open(id: number) {
    nav(`/chat?conv=${id}`);
  }

  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <div className="conversations-page">
      <header>
        <Link to="/chat">← Retour au chat</Link>
        <h1>Toutes les conversations</h1>
      </header>

      <input
        className="search"
        type="search"
        autoFocus
        placeholder="Rechercher dans les titres et les messages…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />

      <div className="result-meta">
        {loading ? 'Chargement…' : `${total} résultat${total > 1 ? 's' : ''}`}
        {debouncedQ && total > 0 && ` pour « ${debouncedQ} »`}
      </div>

      <ul className="conv-list">
        {items.map((c) => (
          <li key={c.id}>
            <button className="open" onClick={() => open(c.id)}>
              <div className="title">{c.title}</div>
              <div className="meta">
                modifiée le {new Date(c.updated_at + 'Z').toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
              </div>
            </button>
            <button className="del" onClick={() => del(c.id)} title="Supprimer">×</button>
          </li>
        ))}
        {!loading && items.length === 0 && (
          <li className="empty">
            {debouncedQ ? 'Aucune conversation ne correspond.' : 'Aucune conversation.'}
          </li>
        )}
      </ul>

      {total > PAGE_SIZE && (
        <div className="pager">
          <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}>← Précédent</button>
          <span>Page {page + 1} / {lastPage + 1}</span>
          <button disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)}>Suivant →</button>
        </div>
      )}
    </div>
  );
}
